// Pure placement maths for the portal-positioned dropdown panels (MultiSelect,
// SearchableMultiSelect). Extracted from MultiSelect.reposition() so both
// components share ONE flip-up + height-clamp implementation, unit-testable in
// node without jsdom layout. All rects are plain numbers in viewport
// coordinates (getBoundingClientRect shape).

export type PanelPlacement = {
  left: number;
  width: number;
  maxHeight: number;
  /** Exactly one of top/bottom is non-null. `bottom` is a CSS `bottom` value
   *  (distance from the viewport's bottom edge) so a flipped panel grows
   *  upward from just above the trigger. */
  top: number | null;
  bottom: number | null;
};

const MARGIN = 8; // keep clear of the boundary edges
const GAP = 4; // gap between trigger and panel
const MIN_HEIGHT = 96; // never squeeze the panel below a usable height

export function placePanel(opts: {
  trigger: { top: number; bottom: number; left: number; width: number };
  /** Nearest scrollable ancestor's rect, or null to bound by the viewport. */
  boundary: { top: number; bottom: number } | null;
  viewport: { width: number; height: number };
  /** Fixed panel width (SearchableMultiSelect); defaults to the trigger width. */
  panelWidth?: number;
  maxPanelHeight: number;
}): PanelPlacement {
  const { trigger, boundary, viewport, maxPanelHeight } = opts;
  const width = opts.panelWidth ?? trigger.width;
  const topBound = Math.max(MARGIN, boundary ? boundary.top : MARGIN);
  const bottomBound = Math.min(
    viewport.height - MARGIN,
    boundary ? boundary.bottom : viewport.height - MARGIN,
  );
  const left = Math.max(MARGIN, Math.min(trigger.left, viewport.width - width - MARGIN));
  const spaceBelow = bottomBound - trigger.bottom - GAP;
  const spaceAbove = trigger.top - topBound - GAP;
  // Flip only when the space below is meaningfully short AND above is better.
  const openUp = spaceBelow < Math.min(maxPanelHeight, 200) && spaceAbove > spaceBelow;
  const maxHeight = Math.max(
    MIN_HEIGHT,
    Math.min(maxPanelHeight, openUp ? spaceAbove : spaceBelow),
  );
  if (openUp) {
    // Pin the panel's bottom just above the trigger so it grows upward.
    return { left, width, maxHeight, top: null, bottom: viewport.height - trigger.top + GAP };
  }
  return { left, width, maxHeight, top: trigger.bottom + GAP, bottom: null };
}
